import { PlayoutService } from './../../../services/playout.service';
import { BBPlayerAPI } from './../../../interfaces/bb-player-api';
import { Observable, Subscription } from 'rxjs';
import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { SapiEntity } from '../../../services/sapi.service';

import { AbstractDetailsComponent } from '../../shared/abstract-details.component';

import { BbTranscodingComponent } from '../../shared/bb-transcoding/bb-transcoding.component';
import { Location } from '@angular/common';
import { Playout } from '../../../interfaces/playout';
import { PublicationService } from '../../../services/publication.service';
import { Publication } from '../../../interfaces/publication';

import { HttpClient } from '@angular/common/http';
import { AuthenticationService } from '../../../services/authentication.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ToCodePipe } from '../../../pipes/toCode.pipe';
import { TabItem } from '../../shared/bb-page-header/bb-page-header.component';

export interface PlayoutSapiEntity extends SapiEntity {
	label: string;
	name: string;
	cornerRadius?: number;
}

@Component({
	selector: 'playout-details',
	templateUrl: './playout-details.component.html',
	styleUrls: ['./playout-details.component.scss']
})

export class PlayoutDetailsComponent extends AbstractDetailsComponent implements AfterViewInit, OnDestroy {

	@ViewChild('formRef', { static: true }) form: NgForm;

	@ViewChild(BbTranscodingComponent) transcodingComponent: BbTranscodingComponent;

	activeTab: string;
	playerApi: BBPlayerAPI;

	demoMediaclip: any;
	demoMediaclipId = 'ovp2575390';
	reloadPlayer = false;
	previousAspectRatio: string = null;

	viewpagePreviewUrl: any;
	viewpageUrl: any;
	plainViewpageUrl: any;

	entity: PlayoutSapiEntity;

	sessionToken = this.authenticationService.getSessionToken();

	previewPlayout: Playout;
	baseUrl: string;
	usedColors: string[];
	format: '16:9' | '4:3' | '21:9' = '16:9';

	tabItems: TabItem[];

	subscriptions: Subscription;

	adServicesRight: boolean;

	constructor(
		protected route: ActivatedRoute,
		protected location: Location,
		protected publicationService: PublicationService,
		protected http: HttpClient,
		protected authenticationService: AuthenticationService,
		protected sanitizer: DomSanitizer,
		private playoutService: PlayoutService,
		protected toCodePipe: ToCodePipe) {

		super(route, location);

		this.entityConfiguration = {
			name: 'playout',
			parentPath: '/library/playouts',
			basePath: '/library/playout/',
			supplementWithDefaults: true,
			defaults: require('../../../resources/defaults/playout.json')
		};

		this.adServicesRight = false;

		this.getPlayoutColors();

		this.subscriptions.add(this.entityLoaded.subscribe(() => {
			this.checkIsnew();
		}));

		this.authenticationService.hasPermission('ad services').subscribe(hasRight => this.adServicesRight = hasRight);

		this.subscriptions = new Subscription();

		this.subscriptions.add(this.publicationService.getData().subscribe((publication: Publication) => {
			if (publication && publication.baseurl) {
				this.baseUrl = publication.baseurl;
				this.updatePlayerDimensions('16:9');
			}
		}));
		this.configurePlayoutTabs();
	}

	configurePlayoutTabs() {
		this.tabItems = [
			{
				title: this.translate.instant('APPEARANCE'),
				routerLink: ['/library/playout', 'appearance'],
				icon: 'eyedropper',
			},
			{
				title: this.translate.instant('CONTROL_BAR'),
				routerLink: ['/library/playout', 'controlbar'],
				icon: 'eject',
			},
			{
				title: this.translate.instant('PLAYER_STATES'),
				routerLink: ['/library/playout', 'start-end-screen'],
				icon: 'play-circle',
			},
			{
				title: this.translate.instant('SHARING'),
				routerLink: ['/library/playout', 'sharing'],
				icon: 'share-alt',
			},
			{
				title: this.translate.instant('BEHAVIOUR'),
				routerLink: ['/library/playout', 'behaviour'],
				icon: 'random',
			},
			{
				title: this.translate.instant('FITMODE'),
				routerLink: ['/library/playout', 'fitmode'],
				icon: 'crop',
			}
		];

		if (this.adServicesRight) {
			this.tabItems.push({
				title: this.translate.instant('ADVERTISING'),
				routerLink: ['/library/playout', 'advertising'],
				icon: 'bullhorn',
			});
		}

		this.tabItems.push({
			title: this.translate.instant('MISCELLANEOUS'),
			routerLink: ['/library/playout', 'miscellaneous'],
			icon: 'caret-square-o-right',
		});
		this.tabItems.push({
			title: this.translate.instant('VIEW_PAGE'),
			routerLink: ['/library/playout', 'viewpage'],
			icon: 'television',
		});

	}

	ngAfterViewInit() {
		// console.warn('this.form', this.form, this.formGroup, this.formChangesSub)
		this.previewPlayout = this.entity as Playout;
		this.subscriptions.add(this.formGroup.valueChanges.subscribe((valueChanges) => {
			this.onFormValueChanges(valueChanges);
		}));
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	private checkIsnew(): void {
		// for existing with cornerRadius undefined it should be 0 (in supplementWithDefaults -> playout.json)
		// for new it should default to 10
		if (this.isNew && !this.entity.id) {
			this.entity.cornerRadius = 10;
		}
	}

	onPlayoutUpdated(playoutUpdates: any) {
		if (playoutUpdates) {
			const playoutCopy = Object.assign({}, playoutUpdates);
			playoutCopy.noStats = 'true';
			playoutCopy.disableKeyboardControls = 'true';
			if (this.playerApi) {
				this.playerApi.updatePlayout(playoutCopy);
			}
			this.playoutService.setPlayout(playoutCopy);
		}
	}

	refreshPlayer() {
		this.reloadPlayer = true;
		setTimeout(() => {
			this.reloadPlayer = false;
		}, 1);
	}

	onFormValueChanges(formValues: any) {
		const name = formValues['playout-name'];
		const playout = (this.entity as Playout);
		if (name && !this.entity.id) {
			playout.label = this.toCodePipe.transform(name);
		}
		this.updatePlayoutFromForm(formValues);
	}

	checkPlayerRefresh() {
		setTimeout(() => {
			const playout = this.entity as Playout;
			if (typeof playout.aspectRatio === 'undefined') {
				return;
			}
			if (this.previousAspectRatio === null) {
				this.previousAspectRatio = playout.aspectRatio;
				return;
			}
			if (playout.aspectRatio !== this.previousAspectRatio) {
				this.refreshPlayer();
				this.previousAspectRatio = playout.aspectRatio;
			}
		}, 1);
	}

	updatePlayoutFromForm(formValues: any) {
		if (this.playerApi) {
			const playoutUpdate: any = {};
			Object.keys(formValues).forEach((key) => {
				if (key.indexOf('playout-') !== -1) {
					const prop = key.replace('playout-', '');
					if (this.entity && this.entity[prop] !== formValues[key] && formValues[key] !== null) {
						playoutUpdate[prop] = formValues[key];
					}
				}
			});

			if (Object.keys(playoutUpdate).length) {
				playoutUpdate.noStats = 'true';
				this.playerApi.updatePlayout(playoutUpdate);
				this.playoutService.setPlayout({...this.entity, ...playoutUpdate});
				this.checkPlayerRefresh();
			}
		}
	}

	playerCreated(playerApi: BBPlayerAPI) {
		// avoid ExpressionChangedAfterItHasBeenCheckedError with timeout
		setTimeout(() => {
			this.playerApi = playerApi;
		});
	}

	load(id: string, duplicating: boolean): Observable<SapiEntity> {
		const loadObservable = super.load(id, duplicating);
		loadObservable.subscribe(() => {
			if (this.entity) {
				this.previewPlayout = this.entity as Playout;
				const playoutCopy = Object.assign({}, this.previewPlayout);
				playoutCopy.noStats = 'true';
				if (this.playerApi) {
					this.playerApi.updatePlayout(playoutCopy);
				}
				this.playoutService.setPlayout(this.previewPlayout);
				this.checkPlayerRefresh();
			}
		});

		return loadObservable;
	}

	getPlayoutColors() {
		const usedColors: any[] = [];

		this.sapiService
			.getUrl('/playout?facet=on&limit=0&facetfield[]=skin_backgroundColor&facetfield[]=skin_foregroundColor&facetfield[]=skin_widgetColor')
			.subscribe((response) => {
				if (response && response.facets) {
					Object.keys(response.facets).forEach((facetKey: any) => {
						const facetValues = response.facets[facetKey];
						for (let i = 0; i < facetValues.length; i += 2) {
							const color = '#' + facetValues[i].toUpperCase();

							if (color !== '#' && usedColors.indexOf(color) === -1) {
								usedColors.push(color);
							}
						}
					});
				}
				this.usedColors = usedColors;
			});
	}

	updatePlayerDimensions(format: '16:9' | '4:3' | '21:9'): void {
		this.format = format;
		switch (format) {
			case '21:9':
				this.demoMediaclipId = 'ovp2575388';
				break;
			case '4:3':
				this.demoMediaclipId = 'ovp2575387';
				break;
			default:
				this.demoMediaclipId = 'ovp2575390';
		}
		this.getDemoMediaclip();
	}

	resetVideo(): void {
		this.refreshPlayer();
	}

	getDemoMediaclip() {
		this.http
			.get(this.baseUrl + '/p/default/c/' + this.demoMediaclipId + '.json?useSession=true', { withCredentials: false })
			.subscribe((response: any) => {
				const clipData = response.clipData;
				if (clipData) {
					this.demoMediaclip = clipData;
				}
			});
	}

	openViewpage() {
		window.open(this.plainViewpageUrl);
	}

	refreshViewpagePreview() {
		this.viewpagePreviewUrl = this.viewpageUrl;
	}

	viewpagePreviewChanged(viewpageUpdate: any) {
		this.plainViewpageUrl = this.baseUrl + '/view/' + this.previewPlayout.label + '/ovp2575390.html?sesstoken=' + this.sessionToken
			+ '&preview=' + viewpageUpdate.code;
		this.viewpageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.plainViewpageUrl);

		if (viewpageUpdate.initial) {
			this.refreshViewpagePreview();
		}

	}
}
